import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import { FormSurface } from '@/shared/operator/FormSurface';
import { MicroLabel } from '@/shared/operator/MicroLabel';

export interface BaseFormShellProps {
  title: string;
  subtitle?: string;
  /** Content before the title (e.g. hierarchy kind icon — Style B). */
  titleAdornment?: React.ReactNode;
  /** Content immediately after the title label (e.g. predefined lock — Style B). */
  titleEndAdornment?: React.ReactNode;
  onClose?: () => void;
  commands?: React.ReactNode;
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
  /** Fill parent height and keep body a flex scroll host (virtualized lists). */
  fullHeight?: boolean;
  /** Hide the visible title (tab already shows it). aria-label stays. */
  hideTitle?: boolean;
}

export const BaseFormShell: React.FC<BaseFormShellProps> = ({
  title,
  subtitle,
  titleAdornment,
  titleEndAdornment,
  onClose,
  commands,
  toolbar,
  footer,
  children,
  disabled = false,
  fullHeight = false,
  hideTitle = false,
}) => {
  const hasTitleChrome =
    titleAdornment != null || titleEndAdornment != null || commands != null || onClose != null
  const showTitleRow = !hideTitle || hasTitleChrome
  const headerContent = (
    <>
      {showTitleRow && (
      <Box
        data-testid="base-form-shell-title-row"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: toolbar != null ? 1 : 0,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        {titleAdornment}
        {!hideTitle && (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h2"
            sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}
            noWrap
          >
            {title}
          </Typography>
          {subtitle != null && subtitle !== '' && (
            <MicroLabel>
              {subtitle}
            </MicroLabel>
          )}
        </Box>
        )}
        {titleEndAdornment}
        <Box sx={{ flex: 1 }} />
        {commands != null && (
          <Box
            data-testid="base-form-shell-commands"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {commands}
          </Box>
        )}
        {onClose != null && (
          <Tooltip title="Close">
            <IconButton
              size="small"
              aria-label="Close"
              onClick={onClose}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      )}

      {toolbar != null && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: 0,
            borderColor: 'divider',
          }}
        >
          {toolbar}
        </Box>
      )}
    </>
  );

  return (
    <FormSurface
      header={headerContent}
      footer={footer}
      fill={fullHeight}
      aria-label={title}
    >
      <Box
        data-testid="base-form-shell-body"
        sx={{
          p: 1.5,
          ...(fullHeight
            ? {
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                flex: 1,
                minHeight: 0,
              }
            : null),
          ...(disabled
            ? {
                pointerEvents: 'none',
                opacity: 0.5,
              }
            : null),
        }}
      >
        {children}
      </Box>
    </FormSurface>
  );
};
